// src/pages/DeliveryRequest.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import Select from "react-select";
import ko from "date-fns/locale/ko";
import { useFormInput, dateFormatToISOString, formatNumberWithCommas } from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { usePostDeliveryOrderMutation } from "@/redux/deliveryApi";
import { useGetAllCarsQuery, useLazySearchCarByNumberQuery, useLazyGetCarByIdQuery } from "@/redux/carApi";
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import {
    useGetCarGubunQuery,
    useGetCarManufacturerQuery,
    useGetCarModelQuery,
    useGetCarModelDetailQuery,
    useGetCarClassQuery,
    useGetCarTrimQuery,
    useGetFuelListQuery,
} from "@/redux/metaApi";
import { originOptions, transmissionOptions } from "@/constants/selectOptions";

export const DeliveryRequest = () => {
    const navigate = useNavigate();
    const { deliveryOrderId } = useParams(); // 만약 수정모드라면 사용하겠지만, 여기서는 CREATE 모드로 가정

    // formData 초기값: POST 요청 body 스펙에 맞춤
    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE",
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        addressPopupTarget: null,
        vehicleListPopupStatus: false,
        // 탁송 신청 필드
        carNumber: "",
        cho: "",
        carGubun: "",
        bmNo: "",
        boiNo: "",
        boNo: "",
        bsNo: "",
        bdNo: "",
        fuel: "",
        startHopeDateTime: null,
        carMemo: "",
        srcAddress: "",
        srcAddressDetail: "",
        srcBCode: "",
        srcPhone: "",
        srcMemo: "",
        destAddress: "",
        destAddressDetail: "",
        destBCode: "",
        destPhone: "",
        destMemo: "",
        riderName: "",
        riderPhone: "",
        riderMemo: "",
        fee: 0,
    });

    const { mode, toastMsg, confirmStatus } = formData;
    const [postDeliveryOrder, { isLoading: isPostLoading }] = usePostDeliveryOrderMutation();
    const [getCarById, { isLoading: carLoading, error: carError }] = useLazyGetCarByIdQuery();

    // 빌드된 request body: 날짜는 ISO 스트링으로 변환
    const buildRequestBody = () => {
        return {
            carNumber: formData.carNumber,
            cho: formData.cho,
            carGubun: formData.carGubun,
            bmNo: formData.bmNo,
            boiNo: formData.boiNo,
            boNo: formData.boNo,
            bsNo: formData.bsNo,
            bdNo: formData.bdNo,
            fuel: formData.fuel,
            startHopeDateTime: formData.startHopeDateTime ? dateFormatToISOString(formData.startHopeDateTime, "full") : "",
            carMemo: formData.carMemo,
            srcAddress: formData.srcAddress,
            srcAddressDetail: formData.srcAddressDetail,
            srcBCode: formData.srcBCode,
            srcPhone: formData.srcPhone,
            srcMemo: formData.srcMemo,
            destAddress: formData.destAddress,
            destAddressDetail: formData.destAddressDetail,
            destBCode: formData.destBCode,
            destPhone: formData.destPhone,
            destMemo: formData.destMemo,
            riderName: formData.riderName,
            riderPhone: formData.riderPhone,
            riderMemo: formData.riderMemo,
            fee: Number(formData.fee),
        };
    };

    const handleSubmit = async (action) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
            confirmStatus: { confirmPopupStatus: false },
        }));
        try {
            const requestBody = buildRequestBody();
            await action(requestBody);
            setFormData((prev) => ({
                ...prev,
                toastMsg: mode === "CREATE" ? "탁송 신청이 완료되었습니다." : "수정이 완료되었습니다.",
                isLoading: false,
            }));
            navigate("/delivery/history");
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
                isLoading: false,
            }));
        }
    };

    const handleSaveConfirm = async () => {
        await handleSubmit((requestBody) => postDeliveryOrder(requestBody).unwrap());
    };

    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `탁송 신청 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    const handleSelectCar = () => {
        setFormData((prev) => ({ ...prev, vehicleListPopupStatus: true }));
    };

    const handleSearchCar = async (carId, contractIndex = 0) => {
        // if (!carId) {
        //     setFormData((prev) => ({
        //         ...prev,
        //         toastMsg: "차량 ID를 입력해주세요.",
        //     }));
        //     return;
        // }

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const res = await getCarById(carId).unwrap();
            const carData = res?.data;
            if (carData) {
                setFormData((prev) => {
                    const updatedContracts = [...prev.contracts];
                    updatedContracts[contractIndex] = {
                        ...updatedContracts[contractIndex],
                        contractCar: carData,
                        carId: carData.id,
                    };
                    return {
                        ...prev,
                        isLoading: false,
                        toastMsg: "차량 조회 완료",
                        contracts: updatedContracts,
                    };
                });
            } else {
                throw new Error("차량 조회 실패");
            }
        } catch (error) {
            console.error("차량 조회 에러:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 실패",
                isLoading: false,
            }));
        }
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "탁송 신청" : "탁송 수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button
                                    type="button"
                                    className="btn btn-delete"
                                    onClick={() => {
                                        // 탁송 삭제 로직 추가
                                    }}
                                >
                                    탁송 삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                {mode === "CREATE" ? "등록" : "수정"}
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">차량 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <VehicleInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            handleSearchCar={() => {
                                                handleSelectCar();
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">출발지 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <DepartureInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">도착지 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <DestinationInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기사/금액 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <PriceInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {formData.vehicleListPopupStatus && (
                <VehicleListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleListPopupStatus: false }))}
                    setFormData={setFormData}
                    onVehicleSelect={(vehicleData) => {
                        getCarById(vehicleData.id)
                            .then((res) => {
                                const carData = res.data?.data;
                                console.log(carData);
                                setFormData((prev) => ({
                                    ...prev,
                                    ...carData,
                                }));
                            })
                            .catch((error) => {
                                console.error("차량 조회 에러:", error);
                            });
                    }}
                />
            )}

            {/* 주소 검색 팝업 */}
            {formData.addressPopupStatus && (
                <SearchAddress
                    modalStatus={formData.addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) => {
                        setFormData((prev) => {
                            switch (prev.addressPopupTarget) {
                                case "SRC":
                                    setFormData((prev) => ({
                                        ...prev,
                                        srcBCode: data.bcode,
                                        srcAddress: data.address,
                                    }));
                                    break;

                                case "DEST":
                                    setFormData((prev) => ({
                                        ...prev,
                                        destBCode: data.bcode,
                                        destAddress: data.address,
                                    }));
                                    break;

                                default:
                                    console.warn("Unknown addressPopupTarget:", prev.addressPopupTarget);
                            }

                            return {
                                ...prev,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                        });
                    }}
                />
            )}
        </>
    );
};

// --------------------------------------------------------
// VehicleInfo Component
// --------------------------------------------------------
const VehicleInfo = ({ formData, handleChange, setFormData, handleSearchCar }) => {
    const {
        cho,
        ownerName,
        carNumber,
        vehicleOrigin,
        carGubun,
        manufacturer,
        model,
        detailModel,
        carClass,
        carTrim,
        fuel,
        transmission,
        motorType,
        color,
        displacement,
        mileage,
        newPrice,
        vin,
        parkingLotBCode,
        bdName,
        bdNo,
        bmName,
        bmNo,
        boName,
        boNo,
        boiName,
        boiNo,
        branchId,
        bsName,
        bsNo,
        carMemo,
    } = formData;

    // 차종 옵션 (독립)
    const { data: carGubunData } = useGetCarGubunQuery();
    const carGubunOptions =
        carGubunData && carGubunData.data
            ? carGubunData.data.map((item) => ({
                  value: item.bgNo,
                  label: item.bgName,
              }))
            : [];

    // 제조사
    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
        })) || [];

    // 모델 (skip: !manufacturer)
    const { data: modelData } = useGetCarModelQuery({ bmNo: bmNo }, { skip: !bmNo });
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
        })) || [];

    // 상세모델 (skip: !model)
    const { data: detailModelData } = useGetCarModelDetailQuery({ boiNo: boiNo }, { skip: !boiNo });
    const detailModelOptions =
        detailModelData?.data?.map((item) => ({
            value: item.boNo,
            label: item.boName,
        })) || [];

    // 등급 (skip: !detailModel)
    const { data: carClassData } = useGetCarClassQuery({ boNo: boNo }, { skip: !boNo });
    const carClassOptions =
        carClassData?.data?.map((item) => ({
            value: item.bsNo,
            label: item.bsName,
        })) || [];

    // 트림 (skip: !carClass)
    const { data: carTrimData } = useGetCarTrimQuery({ bsNo: bsNo }, { skip: !bsNo });
    const carTrimOptions =
        carTrimData?.data?.map((item) => ({
            value: item.bdNo,
            label: item.bdName,
        })) || [];

    // 유종 옵션 : getFuelList API
    const { data: fuelData } = useGetFuelListQuery();
    const fuelOptions =
        fuelData && fuelData.data
            ? fuelData.data.map((item) => ({
                  value: item.bfNo,
                  label: item.bfName,
              }))
            : [];

    // onChange 시 하위 필드를 초기화하는 helper
    const handleSelectChange = (field, option, clearFields = [], label) => {
        setFormData((prev) => ({
            ...prev,
            [field]: label || option.value,
            ...clearFields.reduce((acc, key) => {
                acc[key] = "";
                return acc;
            }, {}),
        }));
    };

    return (
        <>
            {/* 차량번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차량번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="carNumber" placeholder="" value={carNumber || ""} onChange={handleChange} />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() => {
                                handleSearchCar();
                            }}
                        >
                            선택
                        </button>
                    </div>
                </div>
            </div>

            {/* 국산/수입 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>국산/수입</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={originOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="국산/수입 선택"
                            value={originOptions.find((opt) => opt.value === cho)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, cho: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 차종 (독립) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            options={carGubunOptions}
                            isSearchable
                            placeholder="차종 선택"
                            value={carGubunOptions.find((opt) => opt.label === carGubun)}
                            onChange={(option) => handleSelectChange("carGubun", option, [], option.label)}
                        />
                    </div>
                </div>
            </div>

            {/* 제조사 (독립; 제조사 선택 시 하위 초기화) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>제조사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="제조사 선택"
                            options={manufacturerOptions}
                            value={manufacturerOptions?.find((opt) => opt.value === bmNo)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    bmNo: option.value,
                                    boiNo: undefined,
                                    boNo: undefined,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 모델 (제조사 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="모델 선택"
                            options={modelOptions}
                            value={modelOptions?.find((opt) => opt.value === boiNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    boiNo: option.value,
                                    boNo: undefined,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 상세모델 (모델 선택에 종속) */}
            {/* <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>상세모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="상세모델 선택"
                            options={detailModelOptions}
                            value={detailModelOptions?.find((opt) => opt.value === boNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    boNo: option.value,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div> */}

            {/* 등급 (상세모델 선택에 종속) */}
            {/* <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>등급</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="등급 선택"
                            options={carClassOptions}
                            value={carClassOptions?.find((opt) => opt.value === bsNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bsNo: option.value,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div> */}

            {/* 트림 (등급 선택에 종속) */}
            {/* <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>트림</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="트림 선택"
                            options={carTrimOptions}
                            value={carTrimOptions?.find((opt) => opt.value === bdNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bdNo: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div> */}

            {/* 외장색상 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>외장색상</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="화이트" value={color || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 유종 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>유종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유종 선택"
                            value={fuelOptions.find((opt) => opt.label === fuel)}
                            onChange={(option) => {
                                const isElectric = option.label === "전기";
                                setFormData((prev) => ({
                                    ...prev,
                                    fuel: option.label,
                                    displacement: isElectric ? "" : prev.displacement,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 출발시간 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>출발시간</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={formData.startHopeDateTime}
                            onChange={(date) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    startHopeDateTime: date,
                                }));
                            }}
                            showTimeSelect
                            timeIntervals={30}
                            timeCaption="시간"
                            dateFormat="yyyy.MM.dd HH:mm"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>

            {/* 특이사항 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>특이사항</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="carMemo" placeholder="특이사항을 작성해주세요." value={carMemo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};

const DepartureInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const { srcBCode, srcAddress, srcAddressDetail, srcPhone, srcMemo } = formData;

    return (
        <>
            {/* 출발지 주소 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>출발지 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ width: "200px" }}>
                        {/* 우편번호 or 법정동코드 */}
                        <input
                            type="text"
                            name="srcBCode"
                            placeholder=""
                            value={srcBCode || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "SRC",
                                }))
                            }
                        />
                    </div>
                    <div className="form-element" style={{ width: "468px" }}>
                        <input
                            type="text"
                            name="srcAddress"
                            placeholder="주소"
                            value={srcAddress || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "SRC",
                                }))
                            }
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="srcAddressDetail"
                            placeholder="상세 주소"
                            value={srcAddressDetail || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "SRC",
                                }))
                            }
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>

            {/* 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="srcPhone" placeholder="연락처" value={srcPhone || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 비고 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>비고</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="srcMemo" placeholder="비고 내용을 작성해주세요." value={srcMemo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};

const DestinationInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const { destBCode, destAddress, destAddressDetail, destPhone, destMemo } = formData;

    return (
        <>
            {/* 도착지 주소 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>도착지 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ width: "200px" }}>
                        {/* 우편번호 or 법정동코드 */}
                        <input
                            type="text"
                            name="destBCode"
                            placeholder=""
                            value={destBCode || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "DEST",
                                }))
                            }
                        />
                    </div>
                    <div className="form-element" style={{ width: "468px" }}>
                        <input
                            type="text"
                            name="destAddress"
                            placeholder="주소"
                            value={destAddress || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "DEST",
                                }))
                            }
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="destAddressDetail"
                            placeholder="상세 주소"
                            value={destAddressDetail || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "DEST",
                                }))
                            }
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>

            {/* 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="destPhone" placeholder="연락처" value={destPhone || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 비고 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>비고</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="destMemo" placeholder="비고 내용을 작성해주세요." value={destMemo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};

const PriceInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const { riderName, riderPhone, riderMemo, fee } = formData;

    return (
        <>
            {/* 기사 성함 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>기사 성함</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="riderName" placeholder="기사 성함" value={riderName || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 기사 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>기사 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="riderPhone" placeholder="기사 연락처" value={riderPhone || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 탁송 비용 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>탁송 비용</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="riderMemo"
                            placeholder="탁송 비용"
                            value={riderMemo || ""}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                    </div>
                </div>
            </div>

            {/* 탁송사 전달사항 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>탁송사 전달사항</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="fee" placeholder="전달사항을 작성해주세요." value={fee} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};

// 차량 선택
const VehicleListPopup = ({
    layer = true,
    onClose,
    setFormData,
    onVehicleSelect, // 선택한 차량 데이터를 부모로 전달하는 콜백
}) => {
    const [query, setQuery] = useState("");
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedRentCorpId, setSelectedRentCorpId] = useState(null);

    // 사업자 리스트 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: carsData, refetch } = useGetAllCarsQuery({ rent_corp_id: selectedRentCorpId, query }, { skip: !selectedRentCorpId });

    const handleSearch = () => {
        if (!selectedRentCorpId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자를 먼저 선택하세요.",
            }));
            return;
        }
        refetch();
    };

    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
    };

    const handleConfirm = () => {
        if (!selectedVehicle) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량을 선택하세요.",
            }));
            return;
        }
        onVehicleSelect(selectedVehicle);
        onClose();
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup vehicle-list-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">차량 선택</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            {/* 사업자 선택: 선택 시 onRentCorpSelect 호출 */}
                                            <Select
                                                options={corpOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="사업자 선택"
                                                value={corpOptions.find((opt) => opt.value === selectedRentCorpId)}
                                                onChange={(option) => setSelectedRentCorpId(option.value)}
                                            />
                                        </div>
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="차량번호 입력"
                                                value={query || ""}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 차량 리스트 */}
                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"12%"} />
                                            <col width={"17%"} />
                                            <col width={"34%"} />
                                            <col width={"17%"} />
                                            <col width={"20%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">선택</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">차량번호</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">차량모델</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">담당자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">연락처</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {carsData && carsData.data && carsData.data.length > 0 ? (
                                                carsData.data.map((vehicle) => (
                                                    <tr key={vehicle.id}>
                                                        <td>
                                                            <div className="cell" style={{ height: "100%" }}>
                                                                <div className="input-checkbox">
                                                                    <div className="checkbox" style={{ display: "flex" }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="selectedVehicle"
                                                                            id={`selectedVehicle-${vehicle.id}`}
                                                                            checked={selectedVehicle?.id === vehicle.id}
                                                                            onChange={() => handleSelectVehicle(vehicle)}
                                                                        />
                                                                        <label htmlFor={`selectedVehicle-${vehicle.id}`}></label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.carNumber}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.modelName}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.color}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.phone}</div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">차량 데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button type="button" className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="button" className="btn btn-primary btn-confirm" onClick={handleConfirm}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
